import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Label, Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Input, Alert } from 'reactstrap'
import gql from 'graphql-tag'
import Select from 'react-select'
import "./UploadIncidentDocumentModal.css"

const AllIncidentDocumentTypes = gql`
  query AllIncidentDocumentTypes($category: String!) {
    allIncidentDocumentTypes(category: $category, isActive: true) {
      edges {
        node {
          id
          name
          description
          category
        }
      }
    }
  }
`

const CreateIncidentDocument = gql`
  mutation CreateIncidentDocument($input: CreateIncidentDocumentMutationInput!) {
    createIncidentDocument(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const UploadIncidentDocumentModal = ({ incidentId, isOpen, toggle, refetchQuery, documentCategory, slug }) => {
    const { data: incidentDocumentTypes, loading: typesLoading } = useQuery(AllIncidentDocumentTypes, {
        variables: { category: documentCategory },
    })
    const [createIncidentDocument, { loading: createIncidentDocLoading }] = useMutation(CreateIncidentDocument)

    const [selectedType, setSelectedType] = useState(null)
    const [fileList, setFileList] = useState([])
    const [notes, setNotes] = useState('')
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(false)
    const [filePreviews, setFilePreviews] = useState([])
    const [isDragActive, setIsDragActive] = useState(false)
    const fileInputRef = React.useRef(null)

    // Unified file processing function
    const processFiles = (files) => {
        let base64Files = []
        let previews = []
        let filesProcessed = 0

        files.forEach(file => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = function () {
                let docInBase64 = reader.result
                docInBase64 = docInBase64.substring(docInBase64.indexOf(',') + 1)
                base64Files.push(docInBase64)
                previews.push({
                    name: file.name,
                    type: file.type,
                    preview: reader.result,
                })
                filesProcessed++
                if (filesProcessed === files.length) {
                    setFileList(prev => [...prev, ...base64Files])
                    setFilePreviews(prev => [...prev, ...previews])
                }
            }
            reader.onerror = function () {
                filesProcessed++
                if (filesProcessed === files.length) {
                    setFileList(prev => [...prev, ...base64Files])
                    setFilePreviews(prev => [...prev, ...previews])
                }
            }
        })
    }

    const handleFileChange = (e) => {
        processFiles(Array.from(e.target.files))
        e.target.value = null
    }

    // Drag and drop handlers
    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
        processFiles(Array.from(e.dataTransfer.files))
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
    }

    const handleAreaClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const removeFile = (idx) => {
        setFileList(prev => prev.filter((_, i) => i !== idx))
        setFilePreviews(prev => prev.filter((_, i) => i !== idx))
    }

    const handleUpload = async () => {
        setError(null)
        setSuccess(false)
        if (!selectedType) {
            setError('Please select a document type.')
            return
        }
        if (!fileList.length) {
            setError('Please select at least one file to upload.')
            return
        }
        createIncidentDocument({
            variables: {
                input: {
                    incidentId,
                    documentType: selectedType.value,
                    documents: fileList,
                    notes: notes || "",
                    slug: slug,
                },
            },
            onCompleted: (res) => {
                if (res?.createIncidentDocument?.ok) {
                    setSuccess(true)
                    setFileList([])
                    setNotes('')
                    setSelectedType(null)
                    toggle()
                    if (refetchQuery) refetchQuery()
                } else if (res?.createIncidentDocument?.errors) {
                    setError(res.createIncidentDocument.errors?.map(e => e.messages.join(', ')).join('; ') || 'Error uploading document.')
                }
            },
            onError: (err) => {
                setError(err.message)
            },
        })
    }

    const handleModalClosed = () => {
        setError(null)
        setSuccess(false)
        setFileList([])
        setNotes('')
        setSelectedType(null)
    }

    return (
        <Modal isOpen={isOpen} onClosed={handleModalClosed} className="incident-document-modal">
            <ModalHeader>Upload Document</ModalHeader>
            <ModalBody>
                {error && <Row className="mb-2"><Col><Alert color="danger">{error}</Alert></Col></Row>}
                {success && <Row className="mb-2"><Col><Alert color="success">Document uploaded successfully!</Alert></Col></Row>}
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>Document Type</Label>
                            <Select
                                options={incidentDocumentTypes?.allIncidentDocumentTypes?.edges?.map(opt => ({ value: opt.node.id, label: opt.node.name })) || []}
                                value={selectedType}
                                onChange={setSelectedType}
                                isDisabled={typesLoading}
                                placeholder="Select document type..."
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>File</Label><br />
                            <div
                                className={`drag-drop-area${isDragActive ? " drag-drop-active" : ""}`}
                                onClick={handleAreaClick}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                            >
                                {isDragActive ? <span className="drag-drop-anim">Release to upload files</span> : <span>Drag & drop files here or click to select</span>}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                    accept=".pdf,image/*,video/*"
                                    multiple
                                />
                            </div>
                            <div className="selected-files-preview">
                                {filePreviews.map((file, idx) => (
                                    <div key={idx} className="file-preview-container">
                                        <i className="fa fa-times" onClick={() => removeFile(idx)}></i>
                                        {file.type.startsWith('image/') ? (
                                            <img src={file.preview} alt={file.name} className="file-preview-img" />
                                        ) : file.type === 'application/pdf' ? (
                                            <span className="file-preview-pdf">PDF</span>
                                        ) : file.type.startsWith('video/') ? (
                                            <span className="file-preview-video">🎬</span>
                                        ) : (
                                            <span className="file-preview-name">{file.name}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <FormGroup>
                            <Label>Notes</Label>
                            <Input
                                type="textarea"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                rows={3}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button className='incident-document-modal-secondary-btn' onClick={toggle} disabled={createIncidentDocLoading}>Cancel</Button>
                <Button className='incident-document-modal-primary-btn' onClick={handleUpload} disabled={createIncidentDocLoading}>
                    {createIncidentDocLoading ? 'Uploading...' : 'Upload'}
                </Button>
            </ModalFooter>
        </Modal >
    )
}

export default UploadIncidentDocumentModal